import {
  Alert,
  AlertDescription,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  InputGroup,
  PinInput,
  PinInputField,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  Button as ChakraButton,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialog,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Button, ColorfulBar, Input, Select, Textarea } from '~/components';
import { getCities, getStates } from '~/services/States';
import { SelectItem } from '~/types';
import { validateNotificationEmail } from '~/utils/emailRegex';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import {
  getContractExtensionServicesOptions,
  getGenerateCancelContractExtensionToken,
} from '~/services/Extensions';
import { ContractServices } from '~/types/contract-services';
import { STORAGE } from '~/constants';

interface ExtensionRequestFormProps {
  id_contrato: string;
  id_proposta?: number;
  data_encerramento?: string;
}

type FormData = {
  satisfacao: string;
  motivo: string;
  endereco: string;
  numero: string;
  estado: string;
  cidade: string;
  cep: string;
  complemento: string;
  telefone: string;
  email: string;
};

export const CloseContractForm: React.FC<ExtensionRequestFormProps> = ({
  id_contrato,
  id_proposta,
  data_encerramento,
}: ExtensionRequestFormProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
    getValues,
    watch,
  } = useForm();
  const { errors } = formState;

  const [isFetching, setFetching] = useState(false);

  const [servicoInspecao, setServicoInspecao] = useState(false);
  const [servicoDesinstalacao, setServicoDesinstalacao] = useState(false);
  const [servicoEmbalagem, setServicoEmbalagem] = useState(false);
  const [estados, setEstados] = useState<SelectItem[]>([]);
  const [municipios, setMunicipios] = useState<SelectItem[]>([]);
  const estadoValue = watch('estado');
  const [formData, setFormData] = useState<FormData>({} as FormData);
  const history = useHistory();
  const [services, setServices] = useState<ContractServices>(
    {} as ContractServices,
  );
  const user = JSON.parse(localStorage.getItem(`${STORAGE}:user`)!);

  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const {
    isOpen: isOpenAgradecimento,
    onOpen: onOpenAgradecimento,
    onClose: onCloseAgradecimento,
  } = useDisclosure();

  const [codigo, setCodigo] = useState<string>('');

  const options = [
    { value: 0, label: 'Selecione' },
    { value: 1, label: 'Muito insatisfeito' },
    { value: 2, label: 'Insatisfeito' },
    { value: 3, label: 'Indiferente' },
    { value: 4, label: 'Satisfeito' },
    { value: 5, label: 'Muito satisfeito' },
  ];

  const handleOpen = () => {
    getStates().then(response => {
      setEstados(response);
      setValue('estado', response[0].label);
    });
    getCities('AC').then(response => {
      setMunicipios(response);
      setValue('cidade', response[0].value);
    });
    getContractExtensionServicesOptions(id_proposta!).then(response => {
      setServices(response);
    });
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };

  const handleCloseAgradecimento = () => {
    onCloseAgradecimento();
    clearValues();
    onClose();
    history.goBack();
    queryClient.invalidateQueries(['contract', id_contrato]);
  };

  const clearValues = () => {
    setValue('satisfacao', '0');
    setValue('motivo', '');
    setValue('endereco', '');
    setValue('numero', '');
    setValue('cep', '');
    setValue('complemento', '');
    setValue('telefone', '');
    setValue('email', '');
    setServicoInspecao(false);
    setServicoDesinstalacao(false);
    setServicoEmbalagem(false);
    clearErrors();
  };

  const validate = (form: FormData) => {
    const {
      cep,
      cidade,
      complemento,
      email,
      endereco,
      estado,
      motivo,
      numero,
      satisfacao,
      telefone,
    } = form;

    clearErrors();

    satisfacao === '0' &&
      setError('satisfacao', {
        type: 'required',
        message: 'Nível de satisfação é obrigatório',
      });
    !motivo.trim() &&
      setError('motivo', {
        type: 'required',
        message: 'Motivo obrigatório',
      });
    motivo.trim().length > 500 &&
      setError('motivo', {
        type: 'maxLength',
        message: 'Máximo de 500 caracteres',
      });

    !endereco.trim() &&
      setError('endereco', {
        type: 'required',
        message: 'Campo obrigatório',
      });
    endereco.trim().length > 255 &&
      setError('endereco', {
        type: 'maxLength',
        message: 'Máximo de 255 caracteres',
      });
    !numero.trim() &&
      setError('numero', {
        type: 'required',
        message: 'Campo obrigatório',
      });
    numero.trim().length > 10 &&
      setError('numero', {
        type: 'maxLength',
        message: 'Máximo de 10 caracteres',
      });
    !estado &&
      setError('estado', {
        type: 'required',
        message: 'Campo obrigatório',
      });
    !cidade &&
      setError('cidade', {
        type: 'required',
        message: 'Campo obrigatório',
      });
    !cep.trim() &&
      setError('cep', {
        type: 'required',
        message: 'Campo obrigatório',
      });
    complemento.trim().length > 255 &&
      setError('complemento', {
        type: 'maxLength',
        message: 'Máximo de 255 caracteres',
      });

    !telefone.trim() &&
      setError('telefone', {
        type: 'required',
        message: 'Campo obrigatório',
      });
    !email.trim() &&
      setError('email', {
        type: 'required',
        message: 'Campo obrigatório',
      });
    email.trim().length > 255 &&
      setError('email', {
        type: 'maxLength',
        message: 'Máximo de 255 caracteres',
      });
    if (email.trim() && !validateNotificationEmail(email.trim())) {
      setError(`email`, {
        type: 'pattern',
        message: 'E-mail inválido',
      });
    }

    const { _formState } = control;
    return _formState.isValid;
  };

  function handleSubmitForm(form: FormData) {
    if (form && !validate(form)) {
      return;
    }

    form && setFormData(form);

    reeviarToken();
  }

  async function handleCreate() {
    setFetching(true);
    const data = getData();

    const request = api.post(`/encerramento_extensao/?token=${codigo}`, data);
    request
      .then(() => {
        if (!isOpenAgradecimento) {
          onOpenAgradecimento();
        }
        onCloseModal();
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          toast({
            title: `Erro ao encerrar contrato`,
            description: error.response.data,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: `Erro ao encerrar contrato`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  const getData = () => {
    const data = {
      proposta_extensao: id_proposta,
      motivo: formData.motivo,
      satisfacao: formData.satisfacao,
      servico_inspecao: servicoInspecao,
      servico_desinstalacao: servicoDesinstalacao,
      servico_embalagem: servicoEmbalagem,
      municipio_coleta: formData.cidade,
      cep_coleta: formData.cep.replace('-', ''),
      endereco_coleta: formData.endereco,
      numero_coleta: formData.numero,
      telefone: formData.telefone,
      email: formData.email,
      ...(formData.complemento.trim()
        ? { complemento_coleta: formData.complemento.trim() }
        : {}),
    };
    return data;
  };

  useEffect(() => {
    if (estadoValue) {
      getCities(estadoValue).then(response => {
        setMunicipios(response);
      });
    }
  }, [estadoValue]);

  const handleCodigoChange = (value: any) => {
    setCodigo(value);
  };

  const confirmar = () => {
    handleCreate();
  };
  const reeviarToken = () => {
    if (!isOpenModal) {
      onOpenModal();
    }
    setFetching(true);
    getGenerateCancelContractExtensionToken(id_proposta!)
      .then(() => {
        toast({
          title: 'Sucesso',
          description: 'Token enviado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setFetching(false);
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const escondeEmail = (email: string) => {
    if (!email) return '';
    const atIndex = email.indexOf('@');
    if (atIndex >= 4) {
      const hiddenName = '*'.repeat(atIndex - 4) + email.slice(atIndex - 4);
      return `${hiddenName}`;
    }
    return email;
  };

  return (
    <>
      <ChakraButton
        variant="outline"
        onClick={handleOpen}
        isDisabled={isFetching}
        backgroundColor="white"
        borderColor="blackAlpha.500"
      >
        Encerrar contrato
      </ChakraButton>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={false}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader ml="1" mb={4}>
            Encerrar contrato
          </DrawerHeader>
          <DrawerBody mx="1">
            <Stack
              gridGap={6}
              bg="white"
              w={600}
              as="form"
              onSubmit={handleSubmit(handleSubmitForm)}
            >
              <Select
                name="satisfacao"
                fontSize="15px"
                register={register}
                label="Qual o seu nível de satisfação com o serviço? *"
                error={errors?.satisfacao}
                isDisabled={isFetching}
              >
                {options?.map(tipo => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </Select>
              <Textarea
                name="motivo"
                resize="none"
                register={register}
                error={errors?.motivo}
                fontSize="15px"
                label="Gostaria de compartilhar o motivo pelo qual você não irá estender o contrato? *"
                isDisabled={isFetching}
              />

              <Stack gridGap={1}>
                <FormLabel fontSize="15px">
                  Selecione os serviços necessários para coleta:
                </FormLabel>
                <Stack>
                  {services.servico_inspecao && (
                    <Checkbox
                      colorScheme="b"
                      isChecked={servicoInspecao}
                      onChange={() => {
                        setServicoInspecao(!servicoInspecao);
                      }}
                      isDisabled={isFetching}
                    >
                      Inspeção
                    </Checkbox>
                  )}
                  {services.servico_desinstalacao && (
                    <Checkbox
                      colorScheme="b"
                      isChecked={servicoDesinstalacao}
                      onChange={() => {
                        setServicoDesinstalacao(!servicoDesinstalacao);
                      }}
                      isDisabled={isFetching}
                    >
                      Desinstalação
                    </Checkbox>
                  )}
                  {services.servico_embalagem && (
                    <Checkbox
                      colorScheme="b"
                      isChecked={servicoEmbalagem}
                      onChange={() => {
                        setServicoEmbalagem(!servicoEmbalagem);
                      }}
                      isDisabled={isFetching}
                    >
                      Embalagem
                    </Checkbox>
                  )}
                </Stack>
              </Stack>
              <Stack gridGap={3}>
                <Stack>
                  <FormLabel fontSize="15px" margin={0}>
                    Confirme seu endereço de coleta
                  </FormLabel>
                  <Text margin={0} fontSize="14px">
                    Por favor, verifique se o seu endereço está atualizado.
                  </Text>
                </Stack>
                <HStack gridGap={2} alignItems="baseline">
                  <Input
                    name="endereco"
                    type="text"
                    label="Endereço *"
                    register={register}
                    error={errors?.endereco}
                    isDisabled={isFetching}
                  />
                  <Input
                    name="numero"
                    type="text"
                    label="Número *"
                    register={register}
                    error={errors?.numero}
                    isDisabled={isFetching}
                  />
                </HStack>
                <HStack gridGap={2} alignItems="baseline">
                  <Select
                    name="estado"
                    fontSize="15px"
                    register={register}
                    label="Estado *"
                    error={errors?.estado}
                    isDisabled={isFetching}
                  >
                    {estados?.map(tipo => (
                      <option key={tipo.value} value={tipo.label}>
                        {tipo.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    name="cidade"
                    fontSize="15px"
                    register={register}
                    label="Cidade *"
                    error={errors?.cidade}
                    isDisabled={isFetching}
                  >
                    {municipios?.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </Select>
                </HStack>
                <HStack gridGap={2} alignItems="baseline">
                  <Input
                    name="cep"
                    type="text"
                    label="CEP *"
                    register={register}
                    error={errors?.cep}
                    isInputMask
                    mask="99999-999"
                    isDisabled={isFetching}
                  />
                  <Input
                    name="complemento"
                    type="text"
                    label="Complemento"
                    register={register}
                    error={errors?.complemento}
                    isDisabled={isFetching}
                  />
                </HStack>
              </Stack>
              <Stack gridGap={3}>
                <Stack>
                  <FormLabel fontSize="15px" margin={0}>
                    Confirme seus dados de contato
                  </FormLabel>
                  <Text margin={0} fontSize="14px">
                    Por favor, verifique se os seus contatos estão atualizados.
                  </Text>
                </Stack>
                <HStack gridGap={2} alignItems="baseline">
                  <Input
                    name="telefone"
                    type="text"
                    label="Telefone *"
                    register={register}
                    error={errors?.telefone}
                    isInputMask
                    mask="(99) 99999-9999"
                    isDisabled={isFetching}
                  />
                  <Input
                    name="email"
                    type="text"
                    label="E-mail *"
                    register={register}
                    error={errors?.email}
                    isDisabled={isFetching}
                  />
                </HStack>
              </Stack>
              <Alert
                bg="#f7f2fa"
                borderRadius="md"
                color="#9246ce"
                fontSize="sm"
                py={3}
                px={3}
              >
                <AlertDescription>
                  <b>Atenção:</b> Ao confirmar, essa solicitação será realizada
                  na data de encerramento do seu contrato. Caso deseje cancelar
                  essa solicitação, entre em contato através dos nossos canais
                  de atendimento.
                </AlertDescription>
              </Alert>
              <HStack justify="flex-end">
                <Button
                  onClick={handleClose}
                  variant="secondary"
                  isDisabled={isFetching}
                  style={{ marginTop: '20px' }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="white"
                  background="gray.900"
                  _hover={{
                    bgColor: 'black',
                  }}
                  style={{ marginTop: '20px' }}
                  isDisabled={isFetching}
                >
                  Confirmar{' '}
                  {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
                </Button>
              </HStack>
              <Text
                style={{
                  fontSize: '12px',
                  color: 'gray.500',
                }}
              >
                * Campos obrigatórios
              </Text>
              <ConfirmationDialog
                titulo="Tem certeza que deseja encerrar o contrato?"
                onClose={onCloseModal}
                confirmar={confirmar}
                isOpen={isOpenModal}
                isDisabled={codigo.length < 6 || isFetching}
                btnConfirmText="Finalizar"
              >
                <Text>
                  Para confirmar o encerramento, insira o código token enviado
                  para o seu e-mail cadastrado{' '}
                  <ChakraButton color="#1767F3" variant="link" fontWeight={400}>
                    {user ? escondeEmail(user.email) : ''}
                  </ChakraButton>
                  :
                </Text>
                <FormControl isInvalid={errors.codigo}>
                  <InputGroup justifyContent="start">
                    <HStack>
                      <PinInput
                        size="lg"
                        onChange={event => handleCodigoChange(event)}
                        isDisabled={isFetching}
                      >
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                      </PinInput>
                    </HStack>
                  </InputGroup>
                  <HStack marginTop={4} justifyContent="space-between">
                    <ChakraButton
                      color="#5b8af0"
                      variant="link"
                      onClick={reeviarToken}
                      fontSize="sm"
                    >
                      Reenviar token via e-mail{' '}
                    </ChakraButton>
                    {isFetching && <Spinner />}
                  </HStack>
                </FormControl>
              </ConfirmationDialog>
              <AlertDialog
                motionPreset="slideInBottom"
                leastDestructiveRef={undefined}
                onClose={handleCloseAgradecimento}
                isOpen={isOpenAgradecimento}
                isCentered
                size="xl"
              >
                <AlertDialogOverlay />
                <AlertDialogContent>
                  <ColorfulBar />
                  <AlertDialogHeader fontSize="lg">Obrigado!</AlertDialogHeader>
                  <AlertDialogBody>
                    <Stack gridGap={2}>
                      <Text>
                        O seu contrato será encerrado após o término da vigência
                        do contrato em{' '}
                        <span style={{ fontWeight: 'bold' }}>
                          {data_encerramento}
                        </span>
                        .
                      </Text>
                    </Stack>
                  </AlertDialogBody>
                  <AlertDialogFooter>
                    <Button
                      ml={3}
                      onClick={handleCloseAgradecimento}
                      isDisabled={isFetching}
                    >
                      Fechar
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
